import { GameEntity } from "@citychallenge/api-public";
import { GeoLocationEvent } from "../services/geo";
import { useRef, useMemo } from "react";
import { Translation } from "../i18n/translation";
import { sortCoordinates } from "./sort-coordinates";
import { useGamesMapCamera } from "./use-games-map-camera";
import { useDidUpdateEffect } from "../util/use-did-update-effect";
import { LngLat, resolveCoordinate } from "@citychallenge/common";

export type GamesMapZoomTo = "nearest-marker" | "user" | undefined;

export interface GamesMapHelperOptions {
  width: number;
  height: number;
  insetTop?: number;
  insetBottom?: number;
  spacing?: number;
  zoomTo?: GamesMapZoomTo;
  activeFilter?: string | Translation;
  activeGame?: GameEntity;
  cardSize?: number;
  externalCardActive?: boolean;
  externalCardSize?: number;
}

interface CameraChangeEvent {
  centerCoordinate: LngLat;
  padding: {
    top: number;
    bottom: number;
  };
  zoomLevel: number;
  animationMode: "flyTo" | "easeTo" | "linearTo" | "moveTo";
  animationDuration: number;
  pitch?: number;
  heading?: number;
}

export const useGamesMapHelper = (
  games: GameEntity[],
  onCameraChange: (event: CameraChangeEvent) => void,
  options: GamesMapHelperOptions
) => {
  const {
    zoomTo,
    width,
    height,
    insetBottom,
    insetTop,
    spacing,
    cardSize = 0,
    activeGame,
    activeFilter,
    externalCardActive,
    externalCardSize,
  } = options;

  const location = useRef<GeoLocationEvent>();

  const locations = useMemo<number[][]>(() => {
    if (!games.length) return [];

    const coordinates = games
      .map(({ coordinates }) => coordinates?.coordinates)
      .filter((coords) => !!coords) as Array<number[]>;

    if (zoomTo === "user") {
      const result = sortCoordinates(location.current, coordinates);

      if (result) return [result.userLocation, result.sorted[0]];
    }
    return coordinates;
  }, [zoomTo, games]);

  const activeLocation = useMemo(() => {
    if (zoomTo === "nearest-marker") {
      return sortCoordinates(location.current, locations)?.sorted[0];
    }

    return undefined;
  }, [zoomTo]);

  const camera = useGamesMapCamera(locations, {
    width,
    height,
    insetTop,
    insetBottom:
      (insetBottom || 0) +
      (externalCardActive ? externalCardSize || cardSize - 100 : 0),
    spacing,
  });

  const baseZoom = camera?.zoom || 14.5;

  useDidUpdateEffect(() => {
    const { paddingTop = 0, paddingBottom = 0 } = camera || {};

    const location = activeLocation || activeGame?.coordinates?.coordinates;
    if (location) {
      const zoom = baseZoom + 22 / baseZoom;

      onCameraChange({
        padding: {
          top: insetTop || 0,
          bottom: cardSize ? cardSize : insetBottom || 0,
        },
        centerCoordinate: location as LngLat,
        zoomLevel: zoom,
        animationDuration: 700,
        animationMode: "flyTo",
      });
    } else {
      if (camera) {
        const { center, zoom } = camera;

        onCameraChange({
          centerCoordinate: resolveCoordinate(center),
          padding: {
            top: paddingTop,
            bottom: externalCardActive ? cardSize + 60 : paddingBottom,
          },
          zoomLevel: zoom,
          animationMode: "flyTo",
          animationDuration: 1000,
          pitch: 0,
          heading: 0,
        });
      }
    }
  }, [
    activeGame,
    activeLocation,
    activeFilter,
    externalCardActive,
    camera?.center.lat,
    camera?.center.lng,
    camera?.zoom,
  ]);

  return {
    location,
    locations,
    baseZoom,
    camera,
  };
};
